import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { TOTAL_PAGES, getSurahForPage, getScoreClass, formatDate, daysSince, getHeatMapStyle, calculateMemorizationStrength } from '../utils/quranData';
import { MemorizedPage, Profile, Surah } from '../types';
import { pageService, profileService } from '../services/dataService';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

interface PageFormData {
  page_number: number;
  memorized_at: Date;
  reviewed_at: Date;
  score: number;
  notes: string;
}

interface BulkFormData {
  reviewed_at: Date;
  score: number;
  notes: string;
}

interface PagesMap {
  [key: number]: MemorizedPage;
}

function Pages(): React.ReactElement {
  const { profileId } = useParams<{ profileId: string }>();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [pages, setPages] = useState<PagesMap>({});
  const [loading, setLoading] = useState(true);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedPage, setSelectedPage] = useState<number | null>(null);
  const [formData, setFormData] = useState<PageFormData>({
    page_number: 1,
    memorized_at: new Date(),
    reviewed_at: new Date(),
    score: 7,
    notes: ''
  });
  const [activeTab, setActiveTab] = useState(0);
  const [profile, setProfile] = useState<Profile | null>(null);
  
  // Heat map mode state
  const [heatMapMode, setHeatMapMode] = useState(true);
  
  // Multi-select mode state
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [bulkDialogVisible, setBulkDialogVisible] = useState(false);
  const [bulkFormData, setBulkFormData] = useState<BulkFormData>({
    reviewed_at: new Date(),
    score: 7,
    notes: ''
  });
  const [pagesRangeText, setPagesRangeText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const loadData = async (): Promise<void> => {
    try {
      const id = parseInt(profileId || '1');
      
      const profileResponse = await profileService.get(id);
      if (profileResponse.data.success) {
        setProfile(profileResponse.data.profile);
      }
      
      const pagesResponse = await pageService.getAll(id);
      if (pagesResponse.data.success) {
        const pagesMap: PagesMap = {};
        (pagesResponse.data.pages || []).forEach((page: MemorizedPage) => {
          pagesMap[page.page_number] = page;
        });
        setPages(pagesMap);
      }
    } catch (error) {
      console.error('Error loading pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    try {
      const id = parseInt(profileId || '1');
      await pageService.save(id, {
        page_number: formData.page_number,
        memorized_at: formData.memorized_at.toISOString(),
        reviewed_at: formData.reviewed_at.toISOString(),
        score: formData.score,
        notes: formData.notes
      });
      await loadData();
      setDialogVisible(false);
    } catch (error) {
      console.error('Error saving page:', error);
    }
  };

  const handleDelete = async (): Promise<void> => {
    try {
      const id = parseInt(profileId || '1');
      await pageService.delete(id, formData.page_number);
      await loadData();
      setDialogVisible(false);
    } catch (error) {
      console.error('Error deleting page:', error);
    }
  };

  const parsePageRanges = (text: string): number[] => {
    const pageNumbers: Set<number> = new Set();
    const parts = text.split(',').map(p => p.trim()).filter(p => p);
    
    for (const part of parts) {
      if (part.includes('-')) {
        const [start, end] = part.split('-').map(n => parseInt(n.trim()));
        if (!isNaN(start) && !isNaN(end) && start >= 1 && end <= TOTAL_PAGES) {
          for (let i = Math.min(start, end); i <= Math.max(start, end); i++) {
            pageNumbers.add(i);
          }
        }
      } else {
        const num = parseInt(part);
        if (!isNaN(num) && num >= 1 && num <= TOTAL_PAGES) {
          pageNumbers.add(num);
        }
      }
    }
    return Array.from(pageNumbers).sort((a, b) => a - b);
  };

  const pagesToRangeText = (pagesSet: Set<number>): string => {
    const sorted = Array.from(pagesSet).sort((a, b) => a - b);
    if (sorted.length === 0) return '';
    
    const ranges: string[] = [];
    let rangeStart = sorted[0];
    let rangeEnd = sorted[0];
    
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === rangeEnd + 1) {
        rangeEnd = sorted[i];
      } else {
        ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`);
        rangeStart = sorted[i];
        rangeEnd = sorted[i];
      }
    }
    ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`);
    return ranges.join(', ');
  };

  const handleRangeTextChange = (text: string): void => {
    setPagesRangeText(text);
    const parsed = parsePageRanges(text);
    setSelectedPages(new Set(parsed));
  };

  const togglePageSelection = (pageNumber: number): void => {
    const newSelection = new Set(selectedPages);
    if (newSelection.has(pageNumber)) {
      newSelection.delete(pageNumber);
    } else {
      newSelection.add(pageNumber);
    }
    setSelectedPages(newSelection);
    setPagesRangeText(pagesToRangeText(newSelection));
  };

  const handlePageClick = (pageNumber: number): void => {
    if (multiSelectMode) {
      togglePageSelection(pageNumber);
      return;
    }
    
    const existing = pages[pageNumber];
    if (existing) {
      setSelectedPage(pageNumber);
      setFormData({
        page_number: pageNumber,
        memorized_at: new Date(existing.memorized_at),
        reviewed_at: new Date(existing.reviewed_at),
        score: existing.score,
        notes: existing.notes || ''
      });
    } else {
      setSelectedPage(null);
      setFormData({
        page_number: pageNumber,
        memorized_at: new Date(),
        reviewed_at: new Date(),
        score: 7,
        notes: ''
      });
    }
    setDialogVisible(true);
  };

  const toggleMultiSelectMode = (): void => {
    if (multiSelectMode) {
      setSelectedPages(new Set());
      setPagesRangeText('');
    }
    setMultiSelectMode(!multiSelectMode);
  };

  const openBulkEditDialog = (): void => {
    if (selectedPages.size === 0) return;
    setBulkFormData({
      reviewed_at: new Date(),
      score: 7,
      notes: ''
    });
    setBulkDialogVisible(true);
  };

  const handleBulkSave = async (): Promise<void> => {
    if (selectedPages.size === 0) return;
    
    setSaving(true);
    try {
      const id = parseInt(profileId || '1');
      const pageNumbers = Array.from(selectedPages);
      
      // Build array of page data for bulk save
      const pagesData = pageNumbers.map(pageNumber => {
        const existing = pages[pageNumber];
        return {
          page_number: pageNumber,
          memorized_at: existing ? existing.memorized_at : new Date().toISOString(),
          reviewed_at: bulkFormData.reviewed_at.toISOString(),
          score: bulkFormData.score,
          notes: bulkFormData.notes
        };
      });
      
      // Single bulk request instead of multiple individual requests
      await pageService.bulkSave(id, pagesData);
      
      await loadData();
      setBulkDialogVisible(false);
      setMultiSelectMode(false);
      setSelectedPages(new Set());
      setPagesRangeText('');
    } catch (error) {
      console.error('Error saving pages:', error);
    } finally {
      setSaving(false);
    }
  };

  const clearSelection = (): void => {
    setSelectedPages(new Set());
    setPagesRangeText('');
  };

  const needsReview = (page: MemorizedPage | undefined): boolean => {
    if (!page) return false;
    const days = daysSince(page.reviewed_at);
    return days !== null && days > (profile?.max_review_days || 7);
  };

  const getPageStatus = (pageNumber: number): string => {
    const page = pages[pageNumber];
    if (!page) return '';
    if (heatMapMode) return 'heat-map';
    if (needsReview(page)) return 'needs-review';
    return 'memorized';
  };

  const getPageStyle = (pageNumber: number): React.CSSProperties => {
    const page = pages[pageNumber];
    if (!page) return {};
    
    if (heatMapMode) {
      const days = daysSince(page.reviewed_at);
      return getHeatMapStyle(page.score, days, profile?.max_review_days || 7);
    }
    
    return {};
  };

  const getStrengthPercentage = (pageNumber: number): number => {
    const page = pages[pageNumber];
    if (!page) return 0;
    const days = daysSince(page.reviewed_at);
    return Math.round(calculateMemorizationStrength(page.score, days, profile?.max_review_days || 7) * 100);
  };

  const memorizedList = Object.values(pages).sort((a, b) => a.page_number - b.page_number);
  const needsReviewList = memorizedList.filter(needsReview);

  const selectAllNeedsReview = (): void => {
    const needsReviewPages = new Set(needsReviewList.map(p => p.page_number));
    setSelectedPages(needsReviewPages);
    setPagesRangeText(pagesToRangeText(needsReviewPages));
  };

  if (loading) {
    return (
      <div className="flex align-items-center justify-content-center" style={{ height: '50vh' }}>
        <i className="pi pi-spin pi-spinner text-4xl"></i>
      </div>
    );
  }

  const surahForDialog: Surah | undefined = getSurahForPage(formData.page_number);

  return (
    <div>
      <div className="flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold m-0">{profile?.name}</h1>
          <p className="text-color-secondary mt-1 mb-0">
            {Object.keys(pages).length} / {TOTAL_PAGES} {t('dashboard.pagesMemorized')}
          </p>
        </div>
      </div>

      <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
        <TabPanel header={t('nav.pages')}>
          <Card className="mb-4">
            <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h3 className="m-0">{t('nav.pages')}</h3>
              <div className="flex gap-2 align-items-center">
                <Button
                  label={heatMapMode ? (language === 'ar' ? 'عرض عادي' : 'Normal View') : (language === 'ar' ? 'خريطة حرارية' : 'Heat Map')}
                  icon={heatMapMode ? 'pi pi-th-large' : 'pi pi-chart-bar'}
                  className={`p-button-sm ${heatMapMode ? '' : 'p-button-outlined'}`}
                  onClick={() => setHeatMapMode(!heatMapMode)}
                />
                <Button
                  label={multiSelectMode ? t('common.cancel') : (language === 'ar' ? 'تحديد متعدد' : 'Multi-Select')}
                  icon={multiSelectMode ? 'pi pi-times' : 'pi pi-check-square'}
                  className={`p-button-sm ${multiSelectMode ? 'p-button-secondary' : 'p-button-outlined'}`}
                  onClick={toggleMultiSelectMode}
                />
              </div>
            </div>

            {multiSelectMode && (
              <div className="mb-3 p-3 surface-100 border-round">
                <div className="flex flex-column gap-3">
                  <div className="flex align-items-center gap-2 flex-wrap">
                    <label className="font-medium white-space-nowrap">
                      {language === 'ar' ? 'الصفحات:' : 'Pages:'}
                    </label>
                    <InputText
                      value={pagesRangeText}
                      onChange={(e) => handleRangeTextChange(e.target.value)}
                      placeholder={language === 'ar' ? 'مثال: 1-5, 9, 12-14' : 'e.g., 1-5, 9, 12-14'}
                      className="flex-grow-1"
                      style={{ minWidth: '200px' }}
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap align-items-center">
                    <Button
                      label={language === 'ar' ? 'تحتاج مراجعة' : 'Needs Review'}
                      icon="pi pi-clock"
                      className="p-button-sm p-button-outlined p-button-warning"
                      onClick={selectAllNeedsReview}
                      disabled={needsReviewList.length === 0}
                    />
                    <Button
                      label={language === 'ar' ? 'مسح التحديد' : 'Clear'}
                      icon="pi pi-times"
                      className="p-button-sm p-button-outlined p-button-secondary"
                      onClick={clearSelection}
                      disabled={selectedPages.size === 0}
                    />
                    <div className="flex-grow-1"></div>
                    <span className="text-color-secondary">
                      {selectedPages.size} {language === 'ar' ? 'صفحة محددة' : 'pages selected'}
                    </span>
                    <Button
                      label={language === 'ar' ? 'تعديل المحدد' : 'Edit Selected'}
                      icon="pi pi-pencil"
                      className="p-button-sm"
                      onClick={openBulkEditDialog}
                      disabled={selectedPages.size === 0}
                    />
                  </div>
                </div>
              </div>
            )}

            {heatMapMode ? (
              <div className="flex gap-3 align-items-center text-sm mb-3 flex-wrap">
                <span className="text-color-secondary">
                  {language === 'ar' ? 'قوة الحفظ:' : 'Memorization Strength:'}
                </span>
                <div className="heat-map-legend">
                  <span className="text-xs">{language === 'ar' ? 'ضعيف' : 'Weak'}</span>
                  <div className="heat-map-gradient"></div>
                  <span className="text-xs">{language === 'ar' ? 'قوي' : 'Strong'}</span>
                </div>
                <span className="flex align-items-center gap-1">
                  <span className="w-1rem h-1rem border-round" style={{ background: '#e2e8f0', border: '1px solid #cbd5e1' }}></span>
                  {language === 'ar' ? 'غير محفوظ' : 'Not memorized'}
                </span>
                {multiSelectMode && (
                  <span className="flex align-items-center gap-1">
                    <span className="w-1rem h-1rem border-round" style={{ background: '#3b82f6' }}></span>
                    {language === 'ar' ? 'محدد' : 'Selected'}
                  </span>
                )}
              </div>
            ) : (
              <div className="flex gap-3 align-items-center text-sm mb-3 flex-wrap">
                <span className="flex align-items-center gap-1">
                  <span className="w-1rem h-1rem border-round" style={{ background: '#10b981' }}></span>
                  {t('dashboard.pagesMemorized')}
                </span>
                <span className="flex align-items-center gap-1">
                  <span className="w-1rem h-1rem border-round" style={{ background: '#f59e0b' }}></span>
                  {t('dashboard.needsReview')}
                </span>
                {multiSelectMode && (
                  <span className="flex align-items-center gap-1">
                    <span className="w-1rem h-1rem border-round" style={{ background: '#3b82f6' }}></span>
                    {language === 'ar' ? 'محدد' : 'Selected'}
                  </span>
                )}
              </div>
            )}
            
            <div className="page-grid">
              {Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1).map(num => {
                const page = pages[num];
                const strength = page ? getStrengthPercentage(num) : 0;
                const days = page ? daysSince(page.reviewed_at) : null;
                const tooltipText = page 
                  ? `${t('page.number')} ${num} | ${language === 'ar' ? 'القوة' : 'Strength'}: ${strength}% | ${language === 'ar' ? 'الدرجة' : 'Score'}: ${page.score}/10 | ${days} ${language === 'ar' ? 'يوم' : 'days ago'}`
                  : `${t('page.number')} ${num}`;
                
                return (
                  <div
                    key={num}
                    className={`page-cell ${getPageStatus(num)} ${selectedPages.has(num) ? 'selected' : ''}`}
                    style={getPageStyle(num)}
                    onClick={() => handlePageClick(num)}
                    title={tooltipText}
                  >
                    {num}
                  </div>
                );
              })}
            </div>
          </Card>
        </TabPanel>

        <TabPanel header={`${t('dashboard.needsReview')} (${needsReviewList.length})`}>
          <Card>
            {needsReviewList.length === 0 ? (
              <div className="text-center p-4">
                <i className="pi pi-check-circle text-5xl text-primary mb-3"></i>
                <p className="text-color-secondary">{t('common.success')}</p>
              </div>
            ) : (
              <DataTable value={needsReviewList} responsiveLayout="scroll">
                <Column field="page_number" header={t('page.number')} sortable 
                  body={(row: MemorizedPage) => {
                    const surah = getSurahForPage(row.page_number);
                    return (
                      <div>
                        <div className="font-bold">{row.page_number}</div>
                        {surah && (
                          <div className="text-sm text-color-secondary">
                            {language === 'ar' ? surah.nameAr : surah.nameEn}
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
                <Column field="reviewed_at" header={t('page.reviewedAt')} sortable
                  body={(row: MemorizedPage) => (
                    <div>
                      <div>{formatDate(row.reviewed_at)}</div>
                      <div className="text-sm text-orange-500">
                        {daysSince(row.reviewed_at)} {language === 'ar' ? 'يوم' : 'days ago'}
                      </div>
                    </div>
                  )}
                />
                <Column field="score" header={t('page.score')}
                  body={(row: MemorizedPage) => (
                    <span className={`score-badge ${getScoreClass(row.score)}`}>
                      {row.score}
                    </span>
                  )}
                />
                <Column header={language === 'ar' ? 'القوة' : 'Strength'} sortable
                  sortField="strength"
                  body={(row: MemorizedPage) => {
                    const strength = getStrengthPercentage(row.page_number);
                    const days = daysSince(row.reviewed_at) || 0;
                    const style = getHeatMapStyle(row.score, days, profile?.max_review_days || 7);
                    return (
                      <div className="flex align-items-center gap-2">
                        <div 
                          style={{ 
                            width: '40px', 
                            height: '24px', 
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            ...style 
                          }}
                        >
                          {strength}%
                        </div>
                      </div>
                    );
                  }}
                />
                <Column body={(row: MemorizedPage) => (
                  <Button 
                    icon="pi pi-refresh" 
                    className="p-button-rounded p-button-text"
                    onClick={() => handlePageClick(row.page_number)}
                    tooltip={t('page.addReview')}
                  />
                )} />
              </DataTable>
            )}
          </Card>
        </TabPanel>

        <TabPanel header={t('dashboard.pagesMemorized')}>
          <Card>
            <DataTable value={memorizedList} responsiveLayout="scroll" paginator rows={20}>
              <Column field="page_number" header={t('page.number')} sortable 
                body={(row: MemorizedPage) => {
                  const surah = getSurahForPage(row.page_number);
                  return (
                    <div>
                      <div className="font-bold">{row.page_number}</div>
                      {surah && (
                        <div className="text-sm text-color-secondary">
                          {language === 'ar' ? surah.nameAr : surah.nameEn}
                        </div>
                      )}
                    </div>
                  );
                }}
              />
              <Column field="reviewed_at" header={t('page.reviewedAt')} sortable
                body={(row: MemorizedPage) => formatDate(row.reviewed_at)}
              />
              <Column field="score" header={t('page.score')}
                body={(row: MemorizedPage) => (
                  <span className={`score-badge ${getScoreClass(row.score)}`}>
                    {row.score}
                  </span>
                )}
              />
              <Column header={language === 'ar' ? 'القوة' : 'Strength'} sortable
                body={(row: MemorizedPage) => {
                  const strength = getStrengthPercentage(row.page_number);
                  const days = daysSince(row.reviewed_at) || 0;
                  const style = getHeatMapStyle(row.score, days, profile?.max_review_days || 7);
                  return (
                    <div 
                      style={{ 
                        width: '40px', 
                        height: '24px', 
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        ...style 
                      }}
                    >
                      {strength}%
                    </div>
                  );
                }}
              />
              <Column body={(row: MemorizedPage) => (
                <Button 
                  icon="pi pi-pencil" 
                  className="p-button-rounded p-button-text"
                  onClick={() => handlePageClick(row.page_number)}
                />
              )} />
            </DataTable>
          </Card>
        </TabPanel>
      </TabView>

      <Dialog
        header={`${t('page.number')} ${formData.page_number}`}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        style={{ width: '90vw', maxWidth: '450px' }}
      >
        {surahForDialog && (
          <div className="mb-4 p-3 surface-100 border-round">
            <div className="arabic-text text-xl">{surahForDialog.nameAr}</div>
            <div className="text-color-secondary">{surahForDialog.nameEn}</div>
          </div>
        )}

        <div className="field mb-3">
          <label className="block mb-2 font-medium">{t('page.reviewedAt')}</label>
          <Calendar
            value={formData.reviewed_at}
            onChange={(e) => setFormData({ ...formData, reviewed_at: e.value as Date })}
            className="w-full"
            dateFormat="yy-mm-dd"
            showIcon
          />
        </div>

        <div className="field mb-3">
          <label className="block mb-2 font-medium">{t('page.score')}</label>
          <div className="flex flex-wrap gap-2">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
              <Button
                key={score}
                label={score.toString()}
                className={`p-button-rounded ${formData.score === score ? '' : 'p-button-outlined'} ${
                  score <= 3 ? 'p-button-danger' : score <= 6 ? 'p-button-warning' : 'p-button-success'
                }`}
                style={{ width: '2.5rem', height: '2.5rem', padding: 0 }}
                onClick={() => setFormData({ ...formData, score })}
              />
            ))}
          </div>
        </div>

        <div className="field mb-4">
          <label className="block mb-2 font-medium">{t('page.notes')}</label>
          <InputTextarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            className="w-full"
          />
        </div>

        <div className="flex justify-content-between">
          {selectedPage && (
            <Button 
              label={t('common.delete')} 
              icon="pi pi-trash"
              className="p-button-danger p-button-text"
              onClick={handleDelete}
            />
          )}
          <div className="flex gap-2 ml-auto">
            <Button 
              label={t('common.cancel')} 
              className="p-button-text"
              onClick={() => setDialogVisible(false)}
            />
            <Button 
              label={t('common.save')} 
              onClick={handleSave}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        header={language === 'ar' ? `تعديل ${selectedPages.size} صفحة` : `Edit ${selectedPages.size} Pages`}
        visible={bulkDialogVisible}
        onHide={() => setBulkDialogVisible(false)}
        style={{ width: '90vw', maxWidth: '450px' }}
      >
        <div className="mb-4 p-3 surface-100 border-round">
          <div className="text-sm text-color-secondary mb-1">
            {language === 'ar' ? 'الصفحات المحددة:' : 'Selected pages:'}
          </div>
          <div className="font-medium" style={{ wordBreak: 'break-word' }}>
            {pagesRangeText || '-'}
          </div>
        </div>

        <div className="field mb-3">
          <label className="block mb-2 font-medium">{t('page.reviewedAt')}</label>
          <Calendar
            value={bulkFormData.reviewed_at}
            onChange={(e) => setBulkFormData({ ...bulkFormData, reviewed_at: e.value as Date })}
            className="w-full"
            dateFormat="yy-mm-dd"
            showIcon
          />
        </div>

        <div className="field mb-3">
          <label className="block mb-2 font-medium">{t('page.score')}</label>
          <div className="flex flex-wrap gap-2">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
              <Button
                key={score}
                label={score.toString()}
                className={`p-button-rounded ${bulkFormData.score === score ? '' : 'p-button-outlined'} ${
                  score <= 3 ? 'p-button-danger' : score <= 6 ? 'p-button-warning' : 'p-button-success'
                }`}
                style={{ width: '2.5rem', height: '2.5rem', padding: 0 }}
                onClick={() => setBulkFormData({ ...bulkFormData, score })}
              />
            ))}
          </div>
        </div>

        <div className="field mb-4">
          <label className="block mb-2 font-medium">{t('page.notes')}</label>
          <InputTextarea
            value={bulkFormData.notes}
            onChange={(e) => setBulkFormData({ ...bulkFormData, notes: e.target.value })}
            rows={3}
            className="w-full"
          />
        </div>

        <div className="flex gap-2 justify-content-end">
          <Button 
            label={t('common.cancel')} 
            className="p-button-text"
            onClick={() => setBulkDialogVisible(false)}
          />
          <Button 
            label={t('common.save')} 
            icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
            onClick={handleBulkSave}
            disabled={saving}
          />
        </div>
      </Dialog>
    </div>
  );
}

export default Pages;
